import { useEffect, useRef, useState } from "react";
import { ExternalLink, Play } from "lucide-react";
import {
  TELEFONKA_SHOWCASE_COPY,
  TELEFONKA_SHOWCASE_INSTAGRAM_URL,
} from "@/config/videoShowcase";
import { loadInstagramEmbedScript } from "@/lib/instagramEmbed";
import "./VideoShowcase.css";

const SectionHeading = ({ kicker, title, sub }) => (
  <div className="text-center mb-12 md:mb-14 max-w-3xl mx-auto">
    <span className="tk-eyebrow">
      <span className="tk-divider" />
      {kicker}
      <span className="tk-divider" />
    </span>
    <h2 className="font-serif text-4xl sm:text-5xl mt-5 text-[var(--ink)] leading-tight">
      {title}
    </h2>
    {sub && <p className="mt-4 text-[var(--ink-soft)] leading-relaxed">{sub}</p>}
  </div>
);

const Corner = ({ position }) => {
  const positionClasses = {
    topLeft: "top-3 left-3 border-t border-l",
    topRight: "top-3 right-3 border-t border-r",
    bottomLeft: "bottom-3 left-3 border-b border-l",
    bottomRight: "bottom-3 right-3 border-b border-r",
  };

  return (
    <span
      className={`pointer-events-none absolute h-5 w-5 border-[rgba(110,30,43,0.28)] ${positionClasses[position]}`}
      aria-hidden="true"
    />
  );
};

export default function VideoShowcase({ lang = "hu" }) {
  const embedContainerRef = useRef(null);
  const [embedRequested, setEmbedRequested] = useState(false);
  const [embedStatus, setEmbedStatus] = useState("idle");
  const copy = TELEFONKA_SHOWCASE_COPY[lang] || TELEFONKA_SHOWCASE_COPY.hu;

  const labels =
    lang === "hu"
      ? {
          load: "Videó megnyitása",
          loading: "Videó betöltése…",
          privacy: "A videó az Instagramról töltődik be.",
          error: "A videó most nem tölthető be.",
          instagram: "Megnézem Instagramon",
          embeddedPost: "Telefonka videó az Instagramról",
          memoryLine: "hangok • történetek • emlékek",
        }
      : {
          load: "Open video",
          loading: "Loading video…",
          privacy: "The video is loaded from Instagram.",
          error: "The video cannot be loaded right now.",
          instagram: "View on Instagram",
          embeddedPost: "Telefonka video from Instagram",
          memoryLine: "voices • stories • memories",
        };

  useEffect(() => {
    if (!embedRequested) return undefined;

    let cancelled = false;
    let observer;
    let timeoutId;

    const markReadyWhenRendered = () => {
      const iframe = embedContainerRef.current?.querySelector("iframe");
      if (!iframe) return false;

      if (!cancelled) setEmbedStatus("ready");
      observer?.disconnect();
      if (timeoutId) window.clearTimeout(timeoutId);
      return true;
    };

    setEmbedStatus("loading");

    loadInstagramEmbedScript()
      .then((instagramApi) => {
        if (cancelled) return;

        observer = new MutationObserver(markReadyWhenRendered);
        if (embedContainerRef.current) {
          observer.observe(embedContainerRef.current, {
            childList: true,
            subtree: true,
          });
        }

        instagramApi.process();

        if (!markReadyWhenRendered()) {
          timeoutId = window.setTimeout(() => {
            if (!cancelled && !markReadyWhenRendered()) setEmbedStatus("error");
          }, 12000);
        }
      })
      .catch(() => {
        if (!cancelled) setEmbedStatus("error");
      });

    return () => {
      cancelled = true;
      observer?.disconnect();
      if (timeoutId) window.clearTimeout(timeoutId);
    };
  }, [embedRequested]);

  return (
    <section
      id="video"
      data-testid="section-video-showcase"
      className="py-20 md:py-28 tk-section-anchor"
    >
      <div className="max-w-5xl mx-auto px-5 md:px-10">
        <SectionHeading kicker={copy.kicker} title={copy.title} sub={copy.body} />

        <div className="relative mx-auto w-full max-w-[430px] rounded-[30px] border border-[rgba(110,30,43,0.16)] bg-[var(--beige-100)] p-2.5 pb-5 sm:p-3 sm:pb-6 shadow-[0_24px_60px_-35px_rgba(44,31,26,0.45)]">
          <Corner position="topLeft" />
          <Corner position="topRight" />
          <Corner position="bottomLeft" />
          <Corner position="bottomRight" />

          <div className="relative overflow-hidden rounded-[23px] border border-[rgba(110,30,43,0.24)] bg-[var(--beige-50)] p-[3px]">
            {!embedRequested ? (
              <div className="relative flex aspect-video w-full items-center justify-center overflow-hidden rounded-[19px] bg-[var(--ink)] px-6 text-center">
                <div className="flex flex-col items-center">
                  <button
                    type="button"
                    onClick={() => setEmbedRequested(true)}
                    className="flex min-h-11 items-center justify-center gap-2 rounded-full border border-white/70 bg-[var(--beige-50)]/95 px-5 text-sm font-semibold text-[var(--burgundy)] shadow-[0_12px_32px_-14px_rgba(44,31,26,0.65)] transition-transform duration-200 hover:scale-[1.03] focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-4 focus-visible:outline-[var(--beige-50)] motion-reduce:transition-none motion-reduce:hover:scale-100"
                    aria-label={labels.load}
                    data-testid="telefonka-instagram-load"
                  >
                    <Play size={20} fill="currentColor" aria-hidden="true" />
                    <span>{labels.load}</span>
                  </button>
                  <p className="mt-3 text-xs text-white/75">{labels.privacy}</p>
                </div>
              </div>
            ) : (
              <div
                ref={embedContainerRef}
                className="tk-instagram-embed relative min-h-[420px] w-full overflow-hidden rounded-[19px] bg-white"
                data-testid="telefonka-instagram-embed"
                aria-busy={embedStatus === "loading" ? "true" : "false"}
              >
                {embedStatus !== "error" && (
                  <blockquote
                    className="instagram-media"
                    data-instgrm-permalink={TELEFONKA_SHOWCASE_INSTAGRAM_URL}
                    data-instgrm-version="14"
                    aria-label={labels.embeddedPost}
                  >
                    <a
                      href={TELEFONKA_SHOWCASE_INSTAGRAM_URL}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      {labels.instagram}
                    </a>
                  </blockquote>
                )}

                {embedStatus === "loading" && (
                  <div
                    className="absolute inset-0 z-10 flex items-center justify-center bg-[var(--beige-50)] px-6 text-center"
                    role="status"
                    aria-live="polite"
                  >
                    <p className="text-sm text-[var(--ink-soft)]">{labels.loading}</p>
                  </div>
                )}

                {embedStatus === "error" && (
                  <div
                    className="flex min-h-[420px] flex-col items-center justify-center px-6 text-center"
                    role="status"
                    aria-live="polite"
                  >
                    <p className="text-sm text-[var(--ink-soft)]">{labels.error}</p>
                    <a
                      href={TELEFONKA_SHOWCASE_INSTAGRAM_URL}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="mt-4 inline-flex min-h-11 items-center justify-center gap-2 rounded-full border border-[rgba(110,30,43,0.25)] bg-[var(--beige-50)] px-5 text-sm font-semibold text-[var(--burgundy)] transition hover:border-[var(--burgundy)] focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[var(--burgundy)]"
                      data-testid="telefonka-instagram-fallback"
                    >
                      {labels.instagram}
                      <ExternalLink size={16} aria-hidden="true" />
                    </a>
                  </div>
                )}
              </div>
            )}
          </div>

          <div className="px-5 pt-4 text-center">
            <p className="font-serif text-2xl text-[var(--ink)]">Telefonka</p>
            <p className="mt-1 text-[10px] uppercase tracking-[0.2em] text-[var(--ink-soft)]/75">
              {labels.memoryLine}
            </p>
          </div>
        </div>
      </div>
    </section>
  );
}
